import type { AxiosPromise } from 'axios';
import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { ProductsEndpoints } from './productsEndpoints';
import {
    toBarcodeIssuesFixResult,
    toBarcodeIssuesPage,
    toProductDetail,
    toProductExportResponse,
    toProductListResponse,
} from './productsMapping';
import type { IProductsService } from './IProductsService';
import type {
    BarcodeIssuesFixRequest,
    BarcodeIssuesQuery,
    ProductCreateRequest,
    ProductDetailQuery,
    ProductExportQuery,
    ProductListQuery,
    ProductUpdateRequest,
    VariantBarcodesUpdateRequest,
} from './requests';
import type {
    BarcodeFixError,
    BarcodeIssuesFixResultWire,
    BarcodeIssuesPageWire,
    FixBarcodeIssuesResponse,
    GetBarcodeIssuesResponse,
    GetProductDetailResponse,
    GetProductExportResponse,
    GetProductListResponse,
} from './responses';

type ProductsServiceOptions = {
    baseUrl: string;
};

/**
 * `generatedBarcode` goes out only as `true` (bos-api API-207): the filter
 * has no "without" view, and `false` would ask the server for one.
 */
const withGeneratedBarcodeOnlyWhenTrue = <
    T extends { generatedBarcode?: boolean },
>({
    generatedBarcode,
    ...rest
}: T): Omit<T, 'generatedBarcode'> | T =>
    generatedBarcode === true ? { ...rest, generatedBarcode } : rest;

/** The refused fix's `itemIndex`, when the server named an item. */
const itemIndexOf = (body: unknown): number | undefined => {
    const itemIndex =
        typeof body === 'object' && body !== null && 'itemIndex' in body
            ? body.itemIndex
            : undefined;
    return typeof itemIndex === 'number' ? itemIndex : undefined;
};

export class ProductsService implements IProductsService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: ProductsServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public async getProductList(
        query: ProductListQuery = {},
    ): Promise<ApiResponse<GetProductListResponse>> {
        // bos-api binds query keys case-insensitively, so the camelCase
        // keys go out as they are and still bind to the controller's
        // `Page`, `PageSize`, `Search`, `CategoryId`, `LowStock`, ….
        const url = ProductsEndpoints.list();
        const result = await makeApiRequest<GetProductListResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, {
                params: withGeneratedBarcodeOnlyWhenTrue(query),
            }),
        );
        return result.success
            ? {
                  success: true,
                  response: toProductListResponse(result.response),
              }
            : result;
    }

    public async exportProducts(
        query: ProductExportQuery = {},
    ): Promise<ApiResponse<GetProductExportResponse>> {
        // The list's parameters without the paging, written the list's way
        // (repeated keys, PascalCase on the wire).
        const url = ProductsEndpoints.export();
        const result = await makeApiRequest<GetProductExportResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, {
                params: withGeneratedBarcodeOnlyWhenTrue(query),
            }),
        );
        return result.success
            ? {
                  success: true,
                  response: toProductExportResponse(result.response),
              }
            : result;
    }

    public async getProduct(
        id: string,
        query: ProductDetailQuery = {},
    ): Promise<ApiResponse<GetProductDetailResponse>> {
        const url = ProductsEndpoints.detail(id);
        const client = getAxiosClient({ baseURL: this.baseUrl });
        // No branch, no query string: the request every other caller of the
        // detail (the edit form, the purchase pickers) has always sent.
        const result = await makeApiRequest<GetProductDetailResponse>(
            query.branchId
                ? client.get(url, { params: { branchId: query.branchId } })
                : client.get(url),
        );
        return result.success
            ? { success: true, response: toProductDetail(result.response) }
            : result;
    }

    public async createProduct(
        body: ProductCreateRequest,
    ): Promise<ApiResponse<GetProductDetailResponse>> {
        // POST creates the whole graph (product + variants + batches) in one
        // round-trip; the bos-api returns the persisted ProductDetail (201).
        const url = ProductsEndpoints.create();
        const result = await makeApiRequest<GetProductDetailResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, body),
        );
        return result.success
            ? { success: true, response: toProductDetail(result.response) }
            : result;
    }

    public async updateProduct(
        id: string,
        body: ProductUpdateRequest,
    ): Promise<ApiResponse<GetProductDetailResponse>> {
        // Fields-only update — product metadata + existing variants' editable
        // fields. The bos-api returns the updated ProductDetail (200).
        const url = ProductsEndpoints.update(id);
        const result = await makeApiRequest<GetProductDetailResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).put(url, body),
        );
        return result.success
            ? { success: true, response: toProductDetail(result.response) }
            : result;
    }

    public async updateVariantBarcodes(
        productId: string,
        variantId: string,
        body: VariantBarcodesUpdateRequest,
    ): Promise<ApiResponse<GetProductDetailResponse>> {
        // The whole set in one PUT: add, remove and make main differ only in
        // the body. The bos-api returns the updated ProductDetail (200).
        const url = ProductsEndpoints.variantBarcodes(productId, variantId);
        const result = await makeApiRequest<GetProductDetailResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).put(url, body),
        );
        return result.success
            ? { success: true, response: toProductDetail(result.response) }
            : result;
    }

    public async getBarcodeIssues(
        query: BarcodeIssuesQuery,
    ): Promise<ApiResponse<GetBarcodeIssuesResponse>> {
        // No branch is the caller's own scope: the parameter is left out.
        const params = {
            ...(query.branchId ? { branchId: query.branchId } : {}),
            page: query.page,
            pageSize: query.pageSize,
        };
        const result = await makeApiRequest<BarcodeIssuesPageWire>(
            getAxiosClient({ baseURL: this.baseUrl }).get(
                ProductsEndpoints.barcodeIssues(),
                { params },
            ),
        );
        return result.success
            ? { success: true, response: toBarcodeIssuesPage(result.response) }
            : result;
    }

    public async fixBarcodeIssues(
        body: BarcodeIssuesFixRequest,
    ): Promise<ApiResponse<FixBarcodeIssuesResponse>> {
        // `makeApiRequest` keeps only the envelope's known fields, and the
        // refusal names its item beside them: the rejected body is kept on
        // the way through so `itemIndex` reaches the caller.
        let refusedBody: unknown = null;
        const post: AxiosPromise = getAxiosClient({ baseURL: this.baseUrl })
            .post(ProductsEndpoints.fixBarcodeIssues(), {
                items: body.items,
                ...(body.branchId ? { branchId: body.branchId } : {}),
            })
            .catch((error: unknown) => {
                refusedBody = (error as { response?: { data?: unknown } })
                    ?.response?.data;
                throw error;
            });

        const result = await makeApiRequest<BarcodeIssuesFixResultWire>(post);
        if (result.success) {
            return {
                success: true,
                response: toBarcodeIssuesFixResult(result.response),
            };
        }
        const itemIndex = itemIndexOf(refusedBody);
        const error: BarcodeFixError =
            itemIndex === undefined
                ? result.error
                : { ...result.error, itemIndex };
        return { success: false, error };
    }

    public deleteProduct(id: string): Promise<ApiResponse<void>> {
        const url = ProductsEndpoints.delete(id);
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).delete(url),
        );
    }

    public setProductArchived(
        id: string,
        archived: boolean,
    ): Promise<ApiResponse<void>> {
        // Two routes rather than a PATCH with a flag: archiving and
        // unarchiving are separate acts server-side.
        const url = archived
            ? ProductsEndpoints.archive(id)
            : ProductsEndpoints.unarchive(id);
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url),
        );
    }
}
